import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { useState, createContext, useContext, lazy, Suspense } from 'react'
import LoginPage from './pages/LoginPage'
import MesasPage from './pages/MesasPage'
import NotificacoesPedidos from './components/NotificacoesPedidos'
import { setCurrentUser, logout as apiLogout } from './api/client'

// D-96 iter 6 (2026-05-15): code-splitting agressivo — só Login + MesasPage
// + NotificacoesPedidos ficam no bundle inicial (3 rotas mais frequentes).
// Resto é lazy: bundle main desce de ~820 KB pra ~400 KB esperado.

// === Operacionais (entram após Mesas) ===
const MesaPage = lazy(() => import('./pages/MesaPage'))
const ClientePage = lazy(() => import('./pages/ClientePage'))
const FechamentoPage = lazy(() => import('./pages/FechamentoPage'))
const SaidaPage = lazy(() => import('./pages/SaidaPage'))
const ReservasPage = lazy(() => import('./pages/ReservasPage'))
const HistoricoPage = lazy(() => import('./pages/HistoricoPage'))
const MenuPage = lazy(() => import('./pages/MenuPage'))
const MeusValesPage = lazy(() => import('./pages/MeusValesPage'))

// === Admin (só admin acessa, vale pena lazy) ===
const EquipePage = lazy(() => import('./pages/EquipePage'))
const CardapioPage = lazy(() => import('./pages/CardapioPage'))
const ExtrasPage = lazy(() => import('./pages/ExtrasPage'))
const AdminValesPage = lazy(() => import('./pages/AdminValesPage'))
const AdminDeliveryPage = lazy(() => import('./pages/AdminDeliveryPage'))
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'))
const AdminRoadmapPage = lazy(() => import('./pages/AdminRoadmapPage'))
const FinanceiroPage = lazy(() => import('./pages/FinanceiroPage'))
const DREPage = lazy(() => import('./pages/DREPage'))
const ConciliacaoPage = lazy(() => import('./pages/ConciliacaoPage'))
const EstoquePage = lazy(() => import('./pages/EstoquePage'))
const EstoqueReceitasPage = lazy(() => import('./pages/EstoqueReceitasPage'))
const EstoqueListaComprasPage = lazy(() => import('./pages/EstoqueListaComprasPage'))
const ContasAPagarPage = lazy(() => import('./pages/ContasAPagarPage'))
const FechamentoDoDiaPage = lazy(() => import('./pages/FechamentoDoDiaPage'))
// D-159 Batch 1 (2026-05-24)
const DespesasPage = lazy(() => import('./pages/DespesasPage'))
const CategoriasPage = lazy(() => import('./pages/CategoriasPage'))
const FornecedoresPage = lazy(() => import('./pages/FornecedoresPage'))
const CronExecucoesPage = lazy(() => import('./pages/CronExecucoesPage'))
// D-160 (2026-05-25)
const CopaPage = lazy(() => import('./pages/CopaPage'))
// D-161 (2026-05-25) — QR Cliente público + painel admin
const QrClientePage = lazy(() => import('./pages/qr/QrClientePage'))
const AdminQrCodesPage = lazy(() => import('./pages/AdminQrCodesPage'))
const AbrirCaixaPage = lazy(() => import('./pages/AbrirCaixaPage'))
const AvulsoPage = lazy(() => import('./pages/AvulsoPage'))
const TrocarSenhaPage = lazy(() => import('./pages/TrocarSenhaPage'))

// === Delivery (já era lazy desde D-95 Fase 3) ===
const DeliveryPedidosPage = lazy(() => import('./pages/delivery/PedidosPage'))
const DeliveryCardapioDoDiaPage = lazy(() => import('./pages/delivery/CardapioDoDiaPage'))
const DeliveryBebidasPage = lazy(() => import('./pages/delivery/BebidasPage'))
const DeliveryMarmitasPage = lazy(() => import('./pages/delivery/MarmitasPage'))
const DeliveryHistoricoPage = lazy(() => import('./pages/delivery/HistoricoPage'))
const DeliveryImpressaoPage = lazy(() => import('./pages/delivery/ImpressaoPage'))
const DeliveryAdicionaisPage = lazy(() => import('./pages/delivery/AdicionaisPage'))
const DeliveryDashboardSitePage = lazy(() => import('./pages/delivery/DashboardSitePage'))

function LazyFallback() {
  return <div className="min-h-screen bg-cream flex items-center justify-center text-gray-400 text-sm">Carregando…</div>
}

/** Wrapper que envolve elementos lazy com Suspense + fallback consistente. */
function L({ children }: { children: React.ReactNode }) {
  return <Suspense fallback={<LazyFallback />}>{children}</Suspense>
}

interface UserCtx {
  user: any | null
  setUser: (u: any | null) => void
  logout: () => void
}

export const UserContext = createContext<UserCtx>({
  user: null,
  setUser: () => {},
  logout: () => {},
})

export function useUser() {
  return useContext(UserContext)
}

export default function App() {
  const [user, setUserState] = useState<any | null>(() => {
    const saved = sessionStorage.getItem('myros_user')
    if (saved) {
      const u = JSON.parse(saved)
      setCurrentUser(u.id)
      return u
    }
    return null
  })

  const setUser = (u: any | null) => {
    setUserState(u)
    if (u) {
      sessionStorage.setItem('myros_user', JSON.stringify(u))
      setCurrentUser(u.id)
    } else {
      sessionStorage.removeItem('myros_user')
      setCurrentUser(null)
    }
  }

  // D-120: logout deleta sessão server-side (cookie httpOnly + DB) e zera
  // sessionStorage do client. Best-effort: se o backend falhar, ainda limpa
  // local pra UX não travar (próximo login cria nova sessão).
  const logout = () => {
    apiLogout().catch(() => { /* silent — local cleanup é o que importa */ })
    setUser(null)
  }

  const isAdmin = user?.perfil === 'admin'
  const isCaixaOrAdmin = user?.perfil === 'admin' || user?.perfil === 'caixa'

  return (
    <UserContext.Provider value={{ user, setUser, logout }}>
      <BrowserRouter>
        <NotificacoesPedidos />
        <Routes>
          {/* D-161 (2026-05-25) — Rota PÚBLICA /qr/:token (cliente sem login).
              Não usa NavBar nem auth — só token da mesa identifica. */}
          <Route path="/qr/:token" element={<L><QrClientePage /></L>} />

          {/* Eager (bundle inicial) */}
          <Route path="/login" element={<LoginPage />} />
          {/* D-160 hotfix: copa não acessa /mesas — sempre cai em /copa */}
          <Route path="/mesas" element={
            user
              ? (user.perfil === 'copa' ? <Navigate to="/copa" /> : <MesasPage />)
              : <Navigate to="/login" />
          } />

          {/* Abertura de caixa (D-102) — perfil caixa cai aqui se não tem turno aberto */}
          <Route path="/abrir-caixa" element={user ? <L><AbrirCaixaPage /></L> : <Navigate to="/login" />} />

          {/* Venda avulsa / Balcão (D-103) */}
          <Route path="/avulso" element={user ? <L><AvulsoPage /></L> : <Navigate to="/login" />} />

          {/* Trocar senha (D-110) — só caixa/admin */}
          <Route path="/trocar-senha" element={user ? <L><TrocarSenhaPage /></L> : <Navigate to="/login" />} />

          {/* Operacionais (lazy) */}
          <Route path="/mesa/:id" element={user ? <L><MesaPage /></L> : <Navigate to="/login" />} />
          <Route path="/cliente/:comandaId" element={user ? <L><ClientePage /></L> : <Navigate to="/login" />} />
          <Route path="/fechamento/:mesaId" element={user ? <L><FechamentoPage /></L> : <Navigate to="/login" />} />
          <Route path="/saida" element={user ? <L><SaidaPage /></L> : <Navigate to="/login" />} />
          <Route path="/reservas" element={user ? <L><ReservasPage /></L> : <Navigate to="/login" />} />
          <Route path="/historico" element={user ? <L><HistoricoPage /></L> : <Navigate to="/login" />} />
          <Route path="/menu" element={user ? <L><MenuPage /></L> : <Navigate to="/login" />} />
          <Route path="/meus-vales" element={user ? <L><MeusValesPage /></L> : <Navigate to="/login" />} />

          {/* Admin (lazy) */}
          <Route path="/equipe" element={user ? <L><EquipePage /></L> : <Navigate to="/login" />} />
          <Route path="/cardapio" element={user ? <L><CardapioPage /></L> : <Navigate to="/login" />} />
          <Route path="/extras" element={user ? <L><ExtrasPage /></L> : <Navigate to="/login" />} />
          <Route path="/admin" element={isAdmin ? <L><AdminDashboardPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/dashboard" element={isAdmin ? <L><AdminDashboardPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/roadmap" element={isAdmin ? <L><AdminRoadmapPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro" element={isAdmin ? <L><FinanceiroPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/dre" element={isAdmin ? <L><DREPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/conciliacao" element={isAdmin ? <L><ConciliacaoPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/estoque" element={isAdmin ? <L><EstoquePage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/estoque/receitas" element={isAdmin ? <L><EstoqueReceitasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/estoque/lista-compras" element={isAdmin ? <L><EstoqueListaComprasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/contas-a-pagar" element={isAdmin ? <L><ContasAPagarPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/fechamento-do-dia" element={isAdmin ? <L><FechamentoDoDiaPage /></L> : <Navigate to="/mesas" />} />
          {/* D-159 Batch 1 */}
          <Route path="/admin/financeiro/despesas" element={isAdmin ? <L><DespesasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/categorias" element={isAdmin ? <L><CategoriasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/financeiro/fornecedores" element={isAdmin ? <L><FornecedoresPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/cron/execucoes" element={isAdmin ? <L><CronExecucoesPage /></L> : <Navigate to="/mesas" />} />
          {/* D-161: painel admin QR codes */}
          <Route path="/admin/qr-codes" element={isAdmin ? <L><AdminQrCodesPage /></L> : <Navigate to="/mesas" />} />
          {/* D-160: Copa (perfil copa ou admin) */}
          <Route path="/copa" element={user && (user.perfil === 'copa' || user.perfil === 'admin') ? <L><CopaPage /></L> : <Navigate to="/login" />} />
          <Route path="/admin/vales" element={isAdmin ? <L><AdminValesPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery" element={isAdmin ? <L><AdminDeliveryPage /></L> : <Navigate to="/mesas" />} />

          {/* Delivery (lazy) */}
          <Route path="/admin/delivery/pedidos" element={isCaixaOrAdmin ? <L><DeliveryPedidosPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/cardapio-do-dia" element={isAdmin ? <L><DeliveryCardapioDoDiaPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/bebidas" element={isAdmin ? <L><DeliveryBebidasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/marmitas" element={isAdmin ? <L><DeliveryMarmitasPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/historico" element={isCaixaOrAdmin ? <L><DeliveryHistoricoPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/impressao" element={isCaixaOrAdmin ? <L><DeliveryImpressaoPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/adicionais" element={isAdmin ? <L><DeliveryAdicionaisPage /></L> : <Navigate to="/mesas" />} />
          <Route path="/admin/delivery/dashboard" element={isAdmin ? <L><DeliveryDashboardSitePage /></L> : <Navigate to="/mesas" />} />

          {/* D-160 hotfix (25/05): copa cai em /copa, demais em /mesas */}
          <Route path="*" element={<Navigate to={user ? (user.perfil === 'copa' ? '/copa' : user.perfil === 'seguranca' ? '/saida' : '/mesas') : '/login'} />} />
        </Routes>
      </BrowserRouter>
    </UserContext.Provider>
  )
}
